/**
 * Create GET headers
 *
 * @return Object
 */
export const getGetHeaders = () => {
    const headers = {'Cache-Control': 'no-cache'};
    const token = localStorage.getItem('token');
    headers.Authorization = 'Bearer ' + token;
    return headers;
};

/**
 * Sort array of objects alphanumerically
 *
 * @param arr
 * @param sortField
 * @param sortOrder
 */
export const sortArrayByKey = (arr, sortField, sortOrder = 'ASC') => {
    arr.sort(function (a, b) {
        return a[sortField].toString().toLowerCase().localeCompare(b[sortField].toString().toLowerCase());
    });
    if (sortOrder === 'DESC') {
        arr = arr.reverse();
    }
    return arr;
};

/**
 * Find an array item by key/value
 *
 * @param array
 * @param key
 * @param value
 * @return {*}
 */
export const getItemFromArray = (array, key, value) => {
    return array.find(a => a[key] === value);
};

/**
 * Sort date strings by format
 *
 * @param a
 * @param b
 * @param format
 * @param separator
 * @param sortOrder
 * @return {number}
 */
export const sortDate = (a, b, format = 'mm-dd-yyyy', separator = '-', sortOrder = 'ASC') => {
    let aArr = a.split(separator);
    let baArr = b.split(separator);
    let aComp, bComp;
    if (format === 'mm-dd-yyyy') {
        aComp = aArr[2] + separator + aArr[0] + separator + aArr[1];
        bComp = baArr[2] + separator + baArr[0] + separator + baArr[1];
        return (sortOrder === 'DESC') ? bComp - aComp : aComp - bComp;
    }
    return (sortOrder === 'DESC') ? b - a : a - b;
};

/**
 * Sort numbers
 *
 * @param a
 * @param b
 * @param sortOrder
 * @return {number}
 */
export const sortNumber = (a, b, sortOrder = 'ASC') => {
    return (sortOrder === 'DESC') ? b - a : a - b;
};

/**
 * Sort array by object keys and values
 *
 * @param key
 * @param order
 * @return {Function}
 */
export const compareValues = (key, order = 'ASC') => {
    return function (a, b) {
        if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
            return 0;
        }
        const varA = (typeof a[key] === 'string') ? a[key].toUpperCase() : a[key];
        const varB = (typeof b[key] === 'string') ? b[key].toUpperCase() : b[key];
        let comparison = 0;
        if (varA > varB) {
            comparison = 1;
        } else if (varA < varB) {
            comparison = -1;
        }
        return (
            (order === 'DESC') ? (comparison * -1) : comparison
        );
    };
};

/**
 * Convert date string to a formatted string
 *
 * @param datetime
 * @param format
 * @param includeTime
 * @param forceUTC
 * @param offset
 * @return {*}
 */
export const convertDate = (
    datetime,
    format,
    includeTime = true,
    forceUTC = false,
    offset = null
) => {
    const dateObj = new Date(datetime);

    if (offset) {
        let offsetArr = offset.split(':');
        let offsetHour = parseInt(offsetArr[0], 10);
        dateObj.setUTCHours(dateObj.getUTCHours() + offsetHour);
    }

    const mo = forceUTC ? dateObj.getUTCMonth() + 1 : dateObj.getMonth() + 1;
    const dy = forceUTC ? dateObj.getUTCDate() : dateObj.getDate();
    const yy = forceUTC ? dateObj.getUTCFullYear() : dateObj.getFullYear();
    let timeStr = '';
    if (includeTime) {
        let hr = forceUTC ? dateObj.getUTCHours() : dateObj.getHours();
        const ampm = hr > 11 ? 'pm' : 'am';
        if (hr === 0) {
            hr = 12;
        }
        if (hr > 12) {
            hr -= 12;
        }
        let mn = forceUTC ? dateObj.getUTCMinutes() : dateObj.getMinutes();
        let minVal = String('00' + mn).slice(-2);
        timeStr = ' ' + hr + ':' + minVal + ampm;
    }

    let dateStr;
    if (format === 'display') {
        // Format: mm/dd/yyyy hh:mm am
        dateStr = mo + '/' + dy + '/' + yy + timeStr;
    } else if (format === 'query') {
        // Format: yyyy-mm-dd
        let moVal = String('00' + mo).slice(-2);
        let dyVal = String('00' + dy).slice(-2);
        dateStr = yy + '-' + moVal + '-' + dyVal;
    }
    if (isNaN(yy) || isNaN(mo)) {
        dateStr = 'No Date';
    }
    return dateStr;
};

export const convertTimeFromHoursMinutes = (hours, mins) => {
    let hr = parseInt(hours, 10);
    const ampm = hr > 11 ? 'pm' : 'am';
    if (hr === 0) {
        hr = 12;
    }
    if (hr > 12) {
        hr -= 12;
    }
    let minVal = String('00' + mins).slice(-2);
    return hr + ':' + minVal + ampm;
};

/**
 * Convert Start and End Dates to range for Export filename
 *
 * @param startDate
 * @param endDate
 * @return {string}
 */
export const convertStartEndDatesForFile = (startDate, endDate) => {
    let from = new Date(startDate + 'T00:00:00');
    let to = new Date(endDate + 'T00:00:00');
    let fileStr = '';
    let yy = from.getFullYear();
    let mo = String('00' + parseInt(from.getMonth() + 1, 10)).slice(-2);
    let dd = String('00' + from.getDate()).slice(-2);
    fileStr += yy.toString() + mo.toString() + dd.toString() + '-';
    yy = to.getFullYear();
    mo = String('00' + parseInt(to.getMonth() + 1, 10)).slice(-2);
    dd = String('00' + to.getDate()).slice(-2);
    fileStr += yy.toString() + mo.toString() + dd.toString();
    return fileStr;
};

/**
 * Pad numbers less than 10 with a leading zero
 *
 * @param num
 * @return {string}
 */
export const addLeadingZero = num => {
    return String('00' + num).slice(-2);
};

/**
 * Add values from a array column
 *
 * @param list
 * @param col
 * @return {number}
 */
export const addFromColumn = (list, col) => {
    let sum = 0;
    for (let i = 0; i < list.length; i++) {
        const item = list[i][col].toString().replace(/,/g, '');
        sum += parseFloat(item);
    }
    return sum;
};



// WEBPACK FOOTER //
// ./src/Fuse/shared/functions.js